import { isEqual } from 'lodash/fp';
import pt from 'prop-types';
import { Component } from 'react';
import { SUBSCRIBE_CHANNEL, TEXT_DIRECTION_LTR } from '../constants';

interface CreateLocalizedCurrencyValue {
    subscribeChannelKey?: string;
    handlerKey?: string;
}

export interface LocalizedCurrencyValueProps<V, O extends {}> {
    value: V;
    valueCheck?: (value: V) => React.ReactElement | undefined;
    options?: O;
}

export const createLocalizedCurrencyValue = <V, O extends {}>({
    subscribeChannelKey = SUBSCRIBE_CHANNEL,
    handlerKey = 'localizeCurrencyValue',
}: CreateLocalizedCurrencyValue = {}) =>
    class LocalizedCurrencyValue extends Component<LocalizedCurrencyValueProps<V, O>> {
        static propTypes = {
            value: pt.any,
            valueCheck: pt.func,
            options: pt.shape({}),
        };

        public unsubscribe?: () => void;

        declare context: {
            [key in typeof subscribeChannelKey | typeof handlerKey]: Function;
        };
        static contextTypes = {
            [subscribeChannelKey]: pt.func.isRequired,
            [handlerKey]: pt.func.isRequired,
        };

        shouldComponentUpdate = (nextProps: LocalizedCurrencyValueProps<V, O>) => !isEqual(nextProps, this.props);

        componentDidMount() {
            this.unsubscribe = this.context[subscribeChannelKey](() => this.forceUpdate());
        }

        componentWillUnmount() {
            if (this.unsubscribe) {
                this.unsubscribe();
            }
        }

        render() {
            const { value, valueCheck, options, ...other } = this.props;

            let finalValue: React.ReactElement | undefined;
            if (valueCheck) {
                finalValue = valueCheck(value);
            }
            if (finalValue === undefined || !valueCheck) {
                finalValue = this.context[handlerKey](value, options);
            }

            return (
                <span dir={TEXT_DIRECTION_LTR} data-test-id="localizedCurrency" {...other}>
                    {finalValue}
                </span>
            );
        }
    };

export default createLocalizedCurrencyValue();
